import { useEffect, useState } from "react";

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";

//Componentes
import Paginacao from "@/frontend/componentes/Paginacao";
import SpinnerLoading from "@/frontend/componentes/Spinner";

//Common
import { fn_formatarNumero, fn_url_codificarValor } from "@/common/utils/util";
import { fn_formatarData } from "@/common/utils/date";
import { fn_getSituacaoPagamento } from "@/common/siig";

//Util
import { ICONES_BOTAO } from "@/frontend/utils/icones";

//API
import { api_listarPage, ROTA_LISTAR } from "@/frontend/services/api/api_list";
import {
  api_findByPk,
  ROTA_FIND_BY_PK,
} from "@/frontend/services/api/api_find";

//Telas
import TSolicitacao_det from "@/frontend/pages/gestora/solicitacao/consultas/solicitacao/solicitacao_det";
import {
  fn_getJsxIconeTipoPagamento,
  fn_getJsxSituacaoPagamento,
} from "@/frontend/utils/siig/solicitacao";

//Coluna a mais apresentada no detalhamento conforme o card
export const COLUNA_DETALHE_CARD = {
  SITUACAO: "SITUACAO", //Situação do pagamento (cards que somam mais de uma situação)
  DATA_POSSE: "DATA_POSSE",
  DATA_CONFERENCIA: "DATA_CONFERENCIA",
  DATA_PAGAMENTO: "DATA_PAGAMENTO", //Data do pagamento (recebimento, no dashboard de receitas)
  VALOR_CONTA: "VALOR_CONTA", //Valor rateado para a conta do card (card da conta, no dashboard de receitas)
};

/*
 * Apresenta o detalhamento de um card do dashboard: os pagamentos que entraram na contagem do card
 * @card: Dados do card: titulo, subtitulo, count, total, count_solicitacoes, filtros (os mesmos da contagem),
 *        data_inicio e data_fim (período do vencimento consultado) e coluna (COLUNA_DETALHE_CARD ou lista delas, opcional)
 * OBS: Somente consulta. Lista com os mesmos filtros utilizados na consulta que preencheu o card.
 */
function TDashboard_detalhe({ modalOpen, toggleModal, card }) {
  //### USE STATE ###
  const [pageNumber, setPageNumber] = useState(1);
  const [result, setResult] = useState({
    pages: 0,
    count: 0,
    items: [],
    offset: 0,
    limit: 0,
  });

  const [loading, setLoading] = useState(true);

  //Tela "Mais inf" da solicitação / pagamento
  const [modalOpenSolicitacao, setModalOpenSolicitacao] = useState(null);
  const [solicitacaoDet, setSolicitacaoDet] = useState(null);
  const [pagamentoDet, setPagamentoDet] = useState(null);

  //### VARIÁVEIS AUXILIARES ###
  //Qtde de colunas das faixas do cabeçalho
  const QTDE_COLUNAS_SOLICITACAO = 5;
  //Colunas a mais do card (uma ou mais)
  const colunasCard = [].concat(card.coluna || []);
  const qtdeColunasPagamento = 7 + colunasCard.length;

  //Com a coluna Valor conta, as demais colunas a mais ficam antes do Valor, para o Valor ficar ao lado do Valor conta.
  //Sem ela (cards do dashboard de pagamentos), as colunas a mais ficam depois do Valor
  const isComValorConta = colunasCard.includes(COLUNA_DETALHE_CARD.VALOR_CONTA);
  const colunasAntesValor = isComValorConta
    ? colunasCard.filter((coluna) => coluna != COLUNA_DETALHE_CARD.VALOR_CONTA)
    : [];
  const colunasDepoisValor = isComValorConta
    ? [COLUNA_DETALHE_CARD.VALOR_CONTA]
    : colunasCard;

  //### USE EFFECT ###
  //Listagem da tela
  useEffect(() => {
    //Lista os pagamentos do card com paginação
    async function listarPagamentos(page) {
      setLoading(true);

      //Mesmos filtros da contagem do card + período do vencimento (sem data inicial: vencimento até a data final)
      let queryFiltros = card.filtros;
      if (card.data_inicio) {
        queryFiltros += `&data_vencimento_inicio=${fn_url_codificarValor(card.data_inicio)}`;
      }
      queryFiltros += `&data_vencimento_fim=${fn_url_codificarValor(card.data_fim)}`;

      const result = await api_listarPage(
        ROTA_LISTAR.DASHBOARD_PAGAMENTOS,
        page,
        queryFiltros,
      );

      setResult(result);
      setLoading(false);
    }

    listarPagamentos(pageNumber || 1);
  }, [pageNumber]);

  //### FUNÇÕES ###
  //Retorna o título da coluna a mais do card
  function getTituloColunaCard(coluna) {
    switch (coluna) {
      case COLUNA_DETALHE_CARD.SITUACAO:
        return "Situação";
      case COLUNA_DETALHE_CARD.DATA_POSSE:
        return "Posse";
      case COLUNA_DETALHE_CARD.DATA_CONFERENCIA:
        return "Conferência";
      case COLUNA_DETALHE_CARD.DATA_PAGAMENTO:
        return "Data pagamento";
      case COLUNA_DETALHE_CARD.VALOR_CONTA:
        return "Valor conta";
      default:
        return "";
    }
  }

  //Retorna o valor da coluna a mais do card para o pagamento
  function getValorColunaCard(coluna, pag) {
    switch (coluna) {
      case COLUNA_DETALHE_CARD.SITUACAO:
        //return fn_getSituacaoPagamento(pag.situacao)?.label || pag.situacao;
        return fn_getJsxSituacaoPagamento(pag.situacao);
      case COLUNA_DETALHE_CARD.DATA_POSSE:
        return fn_formatarData(pag.data_posse);
      case COLUNA_DETALHE_CARD.DATA_CONFERENCIA:
        //Data da última conferência do pagamento
        return fn_formatarData(pag.conferencias?.at(-1)?.data);
      case COLUNA_DETALHE_CARD.DATA_PAGAMENTO:
        return fn_formatarData(pag.data_pagamento);
      case COLUNA_DETALHE_CARD.VALOR_CONTA:
        return fn_formatarNumero(pag.valor_conta_rateio, 2);
      default:
        return "";
    }
  }

  //Alinhamento da coluna a mais do card: valor à direita, como a coluna Valor; as demais ao centro
  function getAlinhamentoColunaCard(coluna) {
    return coluna == COLUNA_DETALHE_CARD.VALOR_CONTA ? "right" : "center";
  }

  //Apresenta o título da coluna a mais do card
  function renderTituloColunaCard(coluna) {
    return (
      <td
        key={coluna}
        width="8%"
        align={getAlinhamentoColunaCard(coluna)}
        className="table-header"
      >
        {getTituloColunaCard(coluna)}
      </td>
    );
  }

  //Apresenta o valor da coluna a mais do card para o pagamento
  function renderValorColunaCard(coluna, pag) {
    return (
      <td
        key={coluna}
        align={getAlinhamentoColunaCard(coluna)}
        className={
          coluna == COLUNA_DETALHE_CARD.VALOR_CONTA ? "text-nowrap" : ""
        }
      >
        {getValorColunaCard(coluna, pag)}
      </td>
    );
  }

  //### EVENTOS ###
  //OnClick - Botão Mais inf (abre a mesma tela de detalhe da solicitação da Gestora, somente consulta)
  async function handleBotaoDetalharSolicitacao(pagamento) {
    //Obtém os dados da solicitação
    const solicitacao = await api_findByPk(
      ROTA_FIND_BY_PK.SOLICITACAO,
      pagamento.id_solicitacao,
    );

    if (solicitacao) {
      setSolicitacaoDet(solicitacao);
      setPagamentoDet(pagamento);
      setModalOpenSolicitacao(true);
    }
  }

  return (
    <Modal isOpen={modalOpen} backdrop="static" size="xxl">
      <div className="m-1">
        <ModalHeader toggle={toggleModal} className="titulo-tela-modal">
          {card.titulo}
          {card.subtitulo ? ` - ${card.subtitulo}` : ""}
        </ModalHeader>

        <ModalBody>
          {/* RESUMO: os mesmos números do card */}
          <div className="d-flex flex-wrap mb-3">
            <div className="me-4">
              <span className="fw-bold">Vencimento: </span>
              {card.data_inicio
                ? `${card.data_inicio} a ${card.data_fim}`
                : `até ${card.data_fim}`}
            </div>

            <div className="me-4">
              <span className="fw-bold">Pagamentos: </span>
              {card.count}
            </div>

            <div className="me-4">
              <span className="fw-bold">Solicitações: </span>
              {card.count_solicitacoes}
            </div>

            <div>
              <span className="fw-bold">Valor total: </span>
              R$ {fn_formatarNumero(card.total, 2)}
            </div>
          </div>

          <div className="scroll-modal-fixed">
            {(loading && <SpinnerLoading />) || (
              <>
                <Table responsive>
                  <thead>
                    <tr className="tr-slim">
                      <td
                        colSpan={QTDE_COLUNAS_SOLICITACAO}
                        align="center"
                        className="table-header texto-menor"
                      >
                        Solicitação
                      </td>

                      <td
                        colSpan={qtdeColunasPagamento}
                        align="center"
                        className="table-header texto-menor"
                      >
                        Pagamento
                      </td>

                      <td className="table-header"></td>
                    </tr>

                    <tr className="texto-menor">
                      {/* SOLICITAÇÃO */}
                      <td width="7%" align="center" className="table-header">
                        Número
                      </td>

                      <td width="8%" align="center" className="table-header">
                        Data
                      </td>

                      <td className="table-header">Empresa</td>

                      <td className="table-header">Fornecedor</td>

                      <td className="table-header">Área</td>

                      {/* PAGAMENTO */}
                      <td width="3%" align="center" className="table-header">
                        Tipo pag.
                      </td>

                      <td width="6%" align="center" className="table-header">
                        Num. pag.
                      </td>

                      <td width="8%" align="center" className="table-header">
                        Emissão
                      </td>

                      <td width="8%" align="center" className="table-header">
                        Vencimento
                      </td>

                      <td className="table-header">Tipo documento</td>

                      <td width="8%" className="table-header">
                        Num. doc.
                      </td>

                      {colunasAntesValor.map(renderTituloColunaCard)}

                      <td width="9%" align="right" className="table-header">
                        Valor
                      </td>

                      {colunasDepoisValor.map(renderTituloColunaCard)}

                      <td
                        width="5%"
                        align="center"
                        className="table-header text-nowrap"
                      >
                        Mais inf
                      </td>
                    </tr>
                  </thead>

                  <tbody>
                    {result.items.map((pag) => (
                      <tr key={pag.id} className="tr-slim texto-menor">
                        {/* SOLICITAÇÃO */}
                        <td align="center">
                          {fn_formatarNumero(pag.solicitacao?.numero)}
                        </td>

                        <td align="center">
                          {fn_formatarData(pag.solicitacao?.data)}
                        </td>

                        <td>{pag.solicitacao?.empresa?.nome}</td>

                        <td>{pag.solicitacao?.fornecedor?.nome}</td>

                        <td>{pag.solicitacao?.area?.nome}</td>

                        {/* PAGAMENTO */}
                        <td align="center">
                          {fn_getJsxIconeTipoPagamento(
                            pag.solicitacao?.tipo_pagamento,
                          )}
                        </td>

                        <td align="center" className="text-nowrap">
                          <strong>{pag.num_pag}</strong> / {pag.qtde_pagamento}
                        </td>

                        <td align="center">
                          {fn_formatarData(pag.data_emissao)}
                        </td>

                        <td align="center">
                          {fn_formatarData(pag.data_vencimento)}
                        </td>

                        <td>{pag.tipo_documento_novo?.nome}</td>

                        <td>{pag.num_documento}</td>

                        {colunasAntesValor.map((coluna) =>
                          renderValorColunaCard(coluna, pag),
                        )}

                        <td align="right" className="text-nowrap">
                          {fn_formatarNumero(pag.valor_pagamento, 2)}
                        </td>

                        {colunasDepoisValor.map((coluna) =>
                          renderValorColunaCard(coluna, pag),
                        )}

                        <td align="center">
                          <Button
                            name={`btnMaisInf_${pag.id}`}
                            size="sm"
                            color="light"
                            title="Mais inf..."
                            aria-label={`Mais inf da solicitação ${pag.solicitacao?.numero || ""}`}
                            onClick={() => handleBotaoDetalharSolicitacao(pag)}
                          >
                            {ICONES_BOTAO.DETALHAR}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {result.items.length == 0 && (
                  <div className="text-center text-danger">
                    Nenhum pagamento encontrado!
                  </div>
                )}
              </>
            )}
          </div>

          <Paginacao
            result={result}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
          />
        </ModalBody>

        <ModalFooter>
          <Button name="btnFechar" color="secondary" onClick={toggleModal}>
            {ICONES_BOTAO.FECHAR} Fechar
          </Button>
        </ModalFooter>
      </div>

      {/* MODAL DETALHE SOLICITAÇÃO (Mais inf) */}
      {modalOpenSolicitacao && (
        <TSolicitacao_det
          modalOpen={modalOpenSolicitacao}
          toggleModal={() => setModalOpenSolicitacao(!modalOpenSolicitacao)}
          solicitacao={solicitacaoDet}
          numPag={pagamentoDet.num_pag}
        />
      )}
    </Modal>
  );
}

export default TDashboard_detalhe;
